import { Request } from 'api';
import { AxiosResponse } from 'axios';
import { Organization, OrganizationsDashboardSummaryData } from './organizations.types';
import { SimplePaginateResponse } from '../../../../app.types';

interface PaginatedApiResponse {
    data: Array<Organization>;
    links: any; // Replace 'any' with the actual type of 'links'
    meta: any; // Replace 'any' with the actual type of 'meta'
}

type ReturnTypeCollection = Promise<AxiosResponse<PaginatedApiResponse>>;

/**
 * Older admin org metrics UIs sorted by calendar-bucket fields that the API no longer allows.
 * Map them to the closest rolling-window sort keys so requests succeed after backend changes.
 */
const LEGACY_ORG_DASHBOARD_SORT_FIELD: Record<string, string> = {
    uploads_last_3_months: 'uploads_last_90_days',
    uploads_this_month: 'uploads_last_30_days',
    uploads_last_month: 'uploads_last_60_days',
};

function normalizeOrgDashboardSortParam(sort: string | undefined): string | undefined {
    if (!sort) {
        return sort;
    }
    const descending = sort.startsWith('-');
    const base = sort.replace(/^-/, '');
    const mapped = LEGACY_ORG_DASHBOARD_SORT_FIELD[base] ?? base;
    return descending ? `-${mapped}` : mapped;
}

interface GetOrganizationsAndSubscriptionDataParams {
    organization_uuid?: string;
    org_pod_discrepancy?: string;
    tier?: 'all' | 'no_tier' | 'gold' | 'silver' | 'bronze' | '';
    sort?:
        | 'created_at'
        | '-created_at'
        | 'name'
        | '-name'
        | 'athlete_count'
        | '-athlete_count'
        | 'athlete_pod_subscriptions'
        | '-athlete_pod_subscriptions'
        | 'uploads_last_30_days'
        | '-uploads_last_30_days'
        | 'uploads_last_60_days'
        | '-uploads_last_60_days'
        | 'uploads_last_90_days'
        | '-uploads_last_90_days'
        | 'subscription_level'
        | '-subscription_level'
        | 'total_commissions'
        | '-total_commissions'
        | 'marketplace_commissions'
        | '-marketplace_commissions'
        | 'pib_bonus_commissions'
        | '-pib_bonus_commissions'
        | 'residual_commissions'
        | '-residual_commissions'
        | 'check_match_commissions'
        | '-check_match_commissions'
        | 'org_pod_subscriptions'
        | '-org_pod_subscriptions'
        | 'pods_org'
        | '-pods_org'
        | 'pods_athlete'
        | '-pods_athlete'
        | 'amount_past_due'
        | '-amount_past_due';
    page?: number;
    per_page?: number;
    start_date?: string;
    end_date?: string;
    is_problematic_account?: boolean;
    name?: string;
}

const getOrganizationsAndSubscriptionData = (
    params?: GetOrganizationsAndSubscriptionDataParams,
): ReturnTypeCollection => {
    if (params?.tier === 'all') {
        params.tier = '';
    }
    const filterParams: Record<string, any> = {};

    // Only add filter params if they have values
    if (params?.organization_uuid) {
        filterParams['filter[organization_uuid]'] = params.organization_uuid;
    }
    if (params?.tier !== undefined && params.tier !== '') {
        filterParams['filter[tier]'] = params.tier;
    }
    if (params?.org_pod_discrepancy !== undefined) {
        filterParams['filter[org_pod_discrepancy]'] = params.org_pod_discrepancy;
    }
    if (params?.is_problematic_account !== undefined) {
        filterParams['filter[is_problematic_account]'] = params.is_problematic_account;
    }
    if (params?.name && params.name.trim() !== '') {
        filterParams['filter[name]'] = params.name.trim();
    }

    // Always add sort and pagination params
    if (params?.sort) {
        filterParams.sort = normalizeOrgDashboardSortParam(params.sort);
    }
    if (params?.page) {
        filterParams.page = params.page;
    }
    if (params?.per_page) {
        filterParams.per_page = params.per_page;
    }

    // Only add date params if they have values
    if (params?.start_date && params.start_date.trim() !== '') {
        filterParams.start_date = params.start_date;
    }
    if (params?.end_date && params.end_date.trim() !== '') {
        filterParams.end_date = params.end_date;
    }

    return Request.get(`admin/organizations/dashboard`, { params: filterParams });
};

const getOrganizationsDashboardSummary = (
    params?: GetOrganizationsAndSubscriptionDataParams,
): Promise<AxiosResponse<{ data: OrganizationsDashboardSummaryData }>> => {
    if (params?.tier === 'all') {
        params.tier = '';
    }
    const filterParams: Record<string, any> = {};

    if (params?.organization_uuid) {
        filterParams['filter[organization_uuid]'] = params.organization_uuid;
    }
    if (params?.tier !== undefined && params.tier !== '') {
        filterParams['filter[tier]'] = params.tier;
    }
    if (params?.org_pod_discrepancy !== undefined) {
        filterParams['filter[org_pod_discrepancy]'] = params.org_pod_discrepancy;
    }
    if (params?.is_problematic_account !== undefined) {
        filterParams['filter[is_problematic_account]'] = params.is_problematic_account;
    }
    if (params?.name && params.name.trim() !== '') {
        filterParams['filter[name]'] = params.name.trim();
    }
    if (params?.start_date && params.start_date.trim() !== '') {
        filterParams.start_date = params.start_date;
    }
    if (params?.end_date && params.end_date.trim() !== '') {
        filterParams.end_date = params.end_date;
    }

    return Request.get(`admin/organizations/dashboard/summary`, { params: filterParams });
};

const getOrganizationsAndSubscriptionDataCSV = (
    params?: GetOrganizationsAndSubscriptionDataParams,
): Promise<AxiosResponse<any>> => {
    if (params?.tier === 'all') {
        params.tier = '';
    }
    const filterParams = {
        'filter[organization_uuid]': params?.organization_uuid,
        'filter[tier]': params?.tier,
        'filter[org_pod_discrepancy]': params?.org_pod_discrepancy,
        sort: normalizeOrgDashboardSortParam(params?.sort),
        start_date: params?.start_date || undefined,
        end_date: params?.end_date || undefined,
    };
    return Request.get(`admin/organizations/dashboard/export`, {
        params: filterParams,
        responseType: 'blob', // Important: tell axios to expect binary data
    });
};

// New interface and function for advanced filtering
interface AdvancedSearchParams {
    'filter[name]'?: string;
    'filter[tier]'?: string;
    'filter[address.state]'?: string;
    'filter[address.city]'?: string;
    'filter[address.postal_code]'?: string;
    'filter[distance]'?: string;
    perPage?: number;
}

interface SearchOptions {
    name?: string;
    tier?: string;
    state?: string;
    city?: string;
    postalCode?: string;
    distance?: string;
    perPage?: number;
}

const adminGetAllOrganizationsAdvanced = (
    searchOptions: SearchOptions,
): Promise<AxiosResponse<SimplePaginateResponse<Organization>>> => {
    const params: AdvancedSearchParams = {};

    if (searchOptions.name) {
        params['filter[name]'] = searchOptions.name;
    }

    if (searchOptions.tier) {
        if (searchOptions.tier === 'all') {
            searchOptions.tier = '';
        }
        params['filter[tier]'] = searchOptions.tier;
    }

    if (searchOptions.state) {
        params['filter[address.state]'] = searchOptions.state;
    }

    if (searchOptions.city) {
        params['filter[address.city]'] = searchOptions.city;
    }

    if (searchOptions.postalCode) {
        if (searchOptions.distance) {
            params['filter[distance]'] = `${searchOptions.postalCode},${searchOptions.distance}`;
        } else {
            params['filter[address.postal_code]'] = searchOptions.postalCode;
        }
    }

    if (searchOptions.perPage) {
        params.perPage = searchOptions.perPage;
    }

    return Request.get('/admin/organizations', {
        params,
    });
};

const getSubscriptionTrendsCSV = (): Promise<AxiosResponse<string>> => {
    return Request.get(`admin/organizations/subscription-trends-csv`);
};

const getMrrMovementCSV = (): Promise<AxiosResponse<string>> => {
    return Request.get(`admin/organizations/mrr-movement-csv`, {
        responseType: 'text',
    });
};

interface CreateOrganizationParams {
    name: string;
    zip: string;
    type: string;
    associated_user: string;
    associated_user_is_owner: boolean;
    referring_organization?: string;
    parent_organization?: string;
}

const createOrganization = (params: CreateOrganizationParams): Promise<AxiosResponse<any>> => {
    return Request.post('/organizations', params);
};

export {
    getOrganizationsAndSubscriptionData,
    getOrganizationsDashboardSummary,
    getOrganizationsAndSubscriptionDataCSV,
    adminGetAllOrganizationsAdvanced,
    getSubscriptionTrendsCSV,
    getMrrMovementCSV,
    createOrganization,
};

export type { GetOrganizationsAndSubscriptionDataParams, CreateOrganizationParams };
